// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { showError } from './errorSlice';

// Custom baseQuery to handle 401/403 globally
const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE!,
  credentials: 'include',
  prepareHeaders: (headers) => {
    if (process.env.REACT_APP_DISABLE_LOGIN == 'true') {
      headers.set('Mock-User', localStorage.getItem('local_user_id')!);
    }
  },
});

// Shared promise to handle concurrent refresh attempts
let refreshPromise: Promise<any> | null = null;

const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error) {
    // Handle 401/403 with token refresh
    if (result.error.status === 401 || result.error.status === 403) {
      // Don't attempt refresh if this is a refresh request to prevent infinite loops
      const isRefreshRequest = typeof args === 'object' && args.url === '/auth/refresh';

      if (!isRefreshRequest) {
        // If a refresh is already in progress, wait for it
        if (refreshPromise) {
          console.log('Waiting for ongoing session refresh...');
          await refreshPromise;
          // After the refresh completes, retry the original request
          console.log('Refresh completed, retrying original request');
          return await rawBaseQuery(args, api, extraOptions);
        }

        // Start a new refresh
        console.log('Authentication failed, attempting session refresh...');
        refreshPromise = (async () => {
          try {
            const refreshResult = await rawBaseQuery(
              { url: '/auth/refresh', method: 'POST' },
              api,
              extraOptions,
            );
            return refreshResult;
          } finally {
            // Clear the promise when done (success or failure)
            refreshPromise = null;
          }
        })();

        const refreshResult = await refreshPromise;

        // If refresh was successful, retry the original request
        if (!refreshResult.error) {
          console.log('Session refreshed successfully, retrying original request');
          return await rawBaseQuery(args, api, extraOptions);
        } else {
          console.log('Session refresh failed, redirecting to login');
          window.location.assign('/login');
        }
      }
    } else {
      // Handle all other errors with toast notification
      // Display a generic error message and leave UI state intact
      const errorMessage = 'Something went wrong. Please try again.';
      api.dispatch(showError(errorMessage));
      console.error('API Error:', result.error);
    }
  }

  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptyApi = createApi({
  baseQuery: baseQueryWithAuth,
  endpoints: () => ({}),
});
